import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getTenantDomains,
  deleteTenantDomain,
  verifyTenantDomain,
  type TenantDomain,
  type TenantDomainFilters,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

const STATUS_COLORS: Record<string, string> = {
  pending: 'processing',
  verified: 'success',
  failed: 'error',
};

const SSL_COLORS: Record<string, string> = {
  pending: 'default',
  active: 'green',
  expired: 'orange',
  error: 'red',
};

const STATUSES = ['pending', 'verified', 'failed'];

export default function DomainListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: TenantDomainFilters = { ...params };
      if (statusFilter) filters.status = statusFilter;
      return getTenantDomains(filters);
    },
    [statusFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('admin.confirm_delete_domain'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteTenantDomain(id);
            message.success(t('admin.domain_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleVerify = useCallback(
    async (id: string) => {
      try {
        const result = await verifyTenantDomain(id);
        if (result.status === 'verified') {
          message.success(t('admin.domain_verify_success'));
        } else {
          message.warning(t('admin.domain_verify_failed'));
        }
        gridRef.current?.reload();
      } catch {
        message.error(t('admin.domain_verify_failed'));
      }
    },
    [t]
  );

  const columns: ColumnsType<TenantDomain> = useMemo(
    () => [
      {
        title: t('admin.domain_field.domain'),
        dataIndex: 'domain',
        key: 'domain',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('admin.domain_field.is_primary'),
        dataIndex: 'is_primary',
        key: 'is_primary',
        width: 90,
        render: (v: boolean) => (
          <Tag color={v ? 'blue' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('admin.domain_field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (s: string) => (
          <Tag color={STATUS_COLORS[s] ?? 'default'}>
            {t(`admin.domain_status.${s}`, s)}
          </Tag>
        ),
      },
      {
        title: t('admin.domain_field.verification_method'),
        dataIndex: 'verification_method',
        key: 'verification_method',
        width: 150,
        render: (m: string) => t(`admin.domain_verification.${m}`, m),
      },
      {
        title: t('admin.domain_field.ssl_status'),
        dataIndex: 'ssl_status',
        key: 'ssl_status',
        width: 100,
        render: (s: string) => (
          <Tag color={SSL_COLORS[s] ?? 'default'}>
            {t(`admin.ssl_status.${s}`, s)}
          </Tag>
        ),
      },
      {
        title: t('admin.domain_field.verified_at'),
        dataIndex: 'verified_at',
        key: 'verified_at',
        width: 160,
        render: (v: string | null) =>
          v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 140,
        render: (_: unknown, record: TenantDomain) => (
          <Space size="small">
            {canWrite && record.status !== 'verified' && (
              <Button
                type="link"
                size="small"
                icon={<SafetyCertificateOutlined />}
                onClick={() => handleVerify(record.id)}
                title={t('admin.verify_domain')}
              />
            )}
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/domains/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleVerify]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('admin.domain_field.status')}
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); gridRef.current?.reload(); }}
        style={{ width: 150 }}
        options={STATUSES.map((s) => ({
          value: s,
          label: t(`admin.domain_status.${s}`),
        }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/domains/new')}
        >
          {t('admin.create_domain_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AdminNav />
      <h2>{t('admin.domains_title')}</h2>
      <DataGrid<TenantDomain>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('admin.search_domains_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
